/**
 * Browser performance / auth telemetry helpers (New Relic page actions only).
 * Never pass emails, passwords, or tokens into these helpers.
 */

type Nreum = {
  addPageAction?: (name: string, attrs: Record<string, unknown>) => void;
};

export type AuthPageOutcome =
  | "success"
  | "failure"
  | "requires_2fa"
  | "not_found"
  | "denied";

function getNreum(): Nreum | undefined {
  return (window as unknown as { NREUM?: Nreum }).NREUM;
}

export function createRequestId(): string {
  if (typeof crypto !== "undefined" && typeof crypto.randomUUID === "function") {
    return crypto.randomUUID();
  }
  return `req-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
}

export function startTimer(): () => number {
  const start = performance.now();
  return () => Math.round(performance.now() - start);
}

export function recordAuthPageAction(attrs: {
  action: string;
  outcome: AuthPageOutcome;
  duration_ms: number;
  requestId?: string;
  failure_stage?: string;
  role?: string;
  requires_2fa?: boolean;
  cache_hit?: boolean;
  queue_depth?: number;
  status?: number;
  [key: string]: unknown;
}): void {
  const nreum = getNreum();
  if (!nreum?.addPageAction) return;
  nreum.addPageAction("AuthActionCompleted", {
    action: attrs.action,
    outcome: attrs.outcome,
    duration_ms: attrs.duration_ms,
    requestId: attrs.requestId,
    failure_stage: attrs.failure_stage,
    role: attrs.role,
    requires_2fa: attrs.requires_2fa,
    cache_hit: attrs.cache_hit,
    queue_depth: attrs.queue_depth,
    status: attrs.status,
  });
}

/** Headers to correlate a browser auth journey with GraphQL / auth-service. */
export function authCorrelationHeaders(requestId?: string): Record<string, string> {
  return {
    "x-request-id": requestId || createRequestId(),
  };
}

/**
 * Browser console GraphQL/perf logs for local, admin-dev, and admin-stg hosts.
 * Staging builds set NODE_ENV=production, so do not rely on that flag.
 */
export function shouldLogPerfToConsole(): boolean {
  if (typeof window === "undefined") return false;
  const host = window.location.hostname.toLowerCase();
  if (
    host === "localhost" ||
    host === "127.0.0.1" ||
    host.endsWith(".localhost") ||
    host.startsWith("192.168.") ||
    host.startsWith("10.")
  ) {
    return true;
  }
  if (host.includes("-dev.") || host.includes(".dev.") || host.includes("dev.")) {
    return true;
  }
  if (host.includes("-stg.") || host.includes(".stg.") || host.includes("stg.")) {
    return true;
  }
  return false;
}
